import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import type { MetronomeSoundUrls } from "@/components/drumpattern/metronomeSampleLoader";
import { SYNTH_METRONOME_SOUND_ID, type MetronomeSoundOption, type MetronomeSoundsResponse } from "./metronomeSoundTypes";

interface MetronomeSettings {
  bpm: number;
  beatsPerMeasure: number;
  beatValue: number;
  metronomeSounds: MetronomeSoundOption[];
  selectedMetronomeSoundId: string;
  metronomeSoundUrls: MetronomeSoundUrls | null;
  metronomeSoundsLoading: boolean;
  setBpm: (bpm: number) => void;
  setBeatsPerMeasure: (beats: number) => void;
  setBeatValue: (value: number) => void;
  refreshMetronomeSounds: () => Promise<void>;
  selectMetronomeSound: (soundId: string) => Promise<MetronomeSoundUrls | null>;
}

const MetronomeContext = createContext<MetronomeSettings | null>(null);

export function useMetronomeSettings() {
  const ctx = useContext(MetronomeContext);
  if (!ctx) throw new Error("useMetronomeSettings must be used within MetronomeSettingsProvider");
  return ctx;
}

export function MetronomeSettingsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [bpm, setBpmState] = useState(() => {
    try {
      const saved = localStorage.getItem("drumflow-metronome");
      if (saved) return JSON.parse(saved).bpm ?? 120;
    } catch {
      return 120;
    }
    return 120;
  });
  const [beatsPerMeasure, setBeatsPerMeasureState] = useState(() => {
    try {
      const saved = localStorage.getItem("drumflow-metronome");
      if (saved) return JSON.parse(saved).beatsPerMeasure ?? 4;
    } catch {
      return 4;
    }
    return 4;
  });
  const [beatValue, setBeatValueState] = useState(() => {
    try {
      const saved = localStorage.getItem("drumflow-metronome");
      if (saved) return JSON.parse(saved).beatValue ?? 4;
    } catch {
      return 4;
    }
    return 4;
  });
  const [metronomeSounds, setMetronomeSounds] = useState<MetronomeSoundOption[]>([]);
  const [selectedMetronomeSoundId, setSelectedMetronomeSoundId] = useState(SYNTH_METRONOME_SOUND_ID);
  const [metronomeSoundUrls, setMetronomeSoundUrls] = useState<MetronomeSoundUrls | null>(null);
  const [metronomeSoundsLoading, setMetronomeSoundsLoading] = useState(false);

  const applyMetronomeSoundsResponse = useCallback((data: MetronomeSoundsResponse | null | undefined) => {
    setMetronomeSounds(data?.sounds ?? []);
    setSelectedMetronomeSoundId(data?.selectedSoundId ?? SYNTH_METRONOME_SOUND_ID);
    setMetronomeSoundUrls(data?.urls ?? null);
  }, []);

  const persist = useCallback((b: number, bpm: number, bv: number) => {
    localStorage.setItem("drumflow-metronome", JSON.stringify({ bpm, beatsPerMeasure: b, beatValue: bv }));
  }, []);

  const setBpm = useCallback((v: number) => {
    setBpmState(v);
    setBeatsPerMeasureState((b: number) => { setBeatValueState((bv: number) => { persist(b, v, bv); return bv; }); return b; });
  }, [persist]);

  const setBeatsPerMeasure = useCallback((v: number) => {
    setBeatsPerMeasureState(v);
    setBpmState((bpm: number) => { setBeatValueState((bv: number) => { persist(v, bpm, bv); return bv; }); return bpm; });
  }, [persist]);

  const setBeatValue = useCallback((v: number) => {
    setBeatValueState(v);
    setBpmState((bpm: number) => { setBeatsPerMeasureState((b: number) => { persist(b, bpm, v); return b; }); return bpm; });
  }, [persist]);

  const refreshMetronomeSounds = useCallback(async () => {
    if (!user?.id) {
      applyMetronomeSoundsResponse(null);
      return;
    }

    setMetronomeSoundsLoading(true);
    const { data, error } = await supabase.functions.invoke<MetronomeSoundsResponse>("metronome-sounds", {
      body: { action: "list" },
    });
    setMetronomeSoundsLoading(false);

    if (error || data?.error) {
      applyMetronomeSoundsResponse(null);
      return;
    }

    applyMetronomeSoundsResponse(data);
  }, [applyMetronomeSoundsResponse, user?.id]);

  const selectMetronomeSound = useCallback(async (soundId: string) => {
    if (!user?.id) return null;

    const isSynth = soundId === SYNTH_METRONOME_SOUND_ID;
    const { data, error } = await supabase.functions.invoke<MetronomeSoundsResponse>("metronome-sounds", {
      body: {
        action: "select",
        selectionType: isSynth ? "synth" : "sound_set",
        soundSetId: isSynth ? null : soundId,
      },
    });

    if (error || data?.error) {
      throw new Error(data?.error ?? error?.message ?? "Metronom-Sound konnte nicht gespeichert werden");
    }

    applyMetronomeSoundsResponse(data);
    return data?.urls ?? null;
  }, [applyMetronomeSoundsResponse, user?.id]);

  useEffect(() => {
    void refreshMetronomeSounds();
  }, [refreshMetronomeSounds]);

  return (
    <MetronomeContext.Provider
      value={{
        bpm,
        beatsPerMeasure,
        beatValue,
        metronomeSounds,
        selectedMetronomeSoundId,
        metronomeSoundUrls,
        metronomeSoundsLoading,
        setBpm,
        setBeatsPerMeasure,
        setBeatValue,
        refreshMetronomeSounds,
        selectMetronomeSound,
      }}
    >
      {children}
    </MetronomeContext.Provider>
  );
}
